import type { GridEventListener, GridRowId } from '@mui/x-data-grid-premium';
import {
  GRID_DETAIL_PANEL_TOGGLE_FIELD,
  gridExpandedSortedRowIdsSelector,
  isGroupingColumn,
} from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import {
  type MutableRefObject,
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { AppEventProperties, PreviewList } from '~/analytics/events';
import { useAnalytics } from '~/analytics/useAnalytics';

type Trigger = AppEventProperties<'list_preview_opened'>['trigger'];

// Sweeping across rows opens nothing; resting on one does.
const OPEN_DELAY_MS = 300;
const SWITCH_DELAY_MS = 120;
// Lets the pointer reach the panel without it closing.
const CLOSE_DELAY_MS = 200;

export const ROW_PREVIEW_PANEL_ATTRIBUTE = 'data-row-preview-panel';

// Lists that aren't a data grid mark each row with this and its id in `data-id`.
export const PREVIEW_ROW_ATTRIBUTE = 'data-preview-row';
const ROW_SELECTOR = `.MuiDataGrid-row, [${PREVIEW_ROW_ATTRIBUTE}]`;

// While a dialog from the panel is open, hovering the list must not move or close it.
const isOverlayOpen = () => document.querySelector('.MuiModal-root:not(.MuiModal-hidden)') !== null;

const OWNS_KEYS =
  'input:not([type="checkbox"]):not([type="radio"]), textarea, select, [contenteditable="true"], [role="combobox"], [role="dialog"], [role="menu"], [role="listbox"], .MuiPopper-root, .MuiDataGrid-menu, .MuiDataGrid-panel';

const ownsKeys = (target: EventTarget | null) =>
  target instanceof Element && target.closest(OWNS_KEYS) !== null;

// Space presses the panel's own buttons; Esc and the arrows still reach it from there.
const ownsSpace = (target: EventTarget | null) =>
  target instanceof Element && target.closest(`[${ROW_PREVIEW_PANEL_ATTRIBUTE}]`) !== null;

// The grid's own Space: the detail panel toggle, group rows, and Shift+Space selection.
const isGridSpace = (field: string | undefined, shiftKey: boolean) =>
  shiftKey ||
  (field !== undefined && (field === GRID_DETAIL_PANEL_TOGGLE_FIELD || isGroupingColumn(field)));

const cellField = (target: EventTarget | null) =>
  target instanceof Element
    ? target.closest<HTMLElement>('.MuiDataGrid-cell')?.dataset.field
    : undefined;

const isTabbedToControl = (target: EventTarget | null) =>
  target instanceof Element &&
  target.closest('button, a, [role="button"]')?.classList.contains('Mui-focusVisible') === true;

const PEEK_CHANGE_EVENT = 'row-preview-peek-change';

// Tables of the same list would each toggle Peek mode on the same Space press.
const handledSpace = new WeakSet<Event>();

// One panel on screen at a time, across every table on the page.
let closeOpenPanel: (() => void) | null = null;

const readStoredPeek = (storageKey: string) => {
  try {
    return window.localStorage.getItem(storageKey) === 'true';
  } catch {
    return false;
  }
};

export function useRowPreview({
  list,
  enabled,
  keyboard = true,
  scope = 'page',
  canPreview,
  apiRef,
  rootRef,
}: {
  list: PreviewList;
  enabled: boolean;
  keyboard?: boolean;
  // A list owns the whole page; a table among others only reacts while it is in use.
  scope?: 'page' | 'table';
  // Rows that stand for no record, such as an elevator without a car, open nothing.
  canPreview?: (rowId: string) => boolean;
  apiRef?: MutableRefObject<GridApiPremium>;
  // For a list that isn't a data grid: the element holding its marked rows.
  rootRef?: RefObject<HTMLElement | null>;
}) {
  const { track } = useAnalytics();
  const storageKey = `${list}-peek-mode`;
  const [peekEnabled, setPeekEnabledState] = useState(() => readStoredPeek(storageKey));
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);

  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const isOpen = useRef(false);
  const isPinned = useRef(false);
  const stopWatchingPin = useRef<(() => void) | undefined>(undefined);
  const currentId = useRef<string | null>(null);
  const hoveredId = useRef<string | null>(null);

  const closeRef = useRef<() => void>(() => {});

  const isPreviewable = useCallback(
    (rowId: string) => {
      const node = apiRef?.current?.state ? apiRef.current.getRowNode(rowId) : null;
      if (node && node.type !== 'leaf') return false;
      return canPreview ? canPreview(rowId) : true;
    },
    [apiRef, canPreview],
  );

  const ownsElement = useCallback(
    (target: EventTarget | null) => {
      const root = apiRef?.current?.rootElementRef?.current ?? rootRef?.current;
      return Boolean(root && target instanceof Node && root.contains(target));
    },
    [apiRef, rootRef],
  );

  const markedRows = useCallback(
    () =>
      Array.from(
        rootRef?.current?.querySelectorAll<HTMLElement>(`[${PREVIEW_ROW_ATTRIBUTE}]`) ?? [],
      ),
    [rootRef],
  );

  const clearTimers = useCallback(() => {
    clearTimeout(openTimer.current);
    clearTimeout(closeTimer.current);
  }, []);

  const releasePin = useCallback(() => {
    isPinned.current = false;
    stopWatchingPin.current?.();
    stopWatchingPin.current = undefined;
  }, []);

  useEffect(
    () => () => {
      clearTimers();
      stopWatchingPin.current?.();
      if (closeOpenPanel === closeRef.current) closeOpenPanel = null;
    },
    [clearTimers],
  );

  const show = useCallback(
    (id: string, trigger: Trigger) => {
      if (!isOpen.current) {
        track('list_preview_opened', { list, trigger, record_id: id });
        if (closeOpenPanel !== closeRef.current) closeOpenPanel?.();
        closeOpenPanel = closeRef.current;
      }
      isOpen.current = true;
      currentId.current = id;
      setPreviewId(id);
    },
    [list, track],
  );

  const close = useCallback(() => {
    clearTimers();
    releasePin();
    isOpen.current = false;
    currentId.current = null;
    setPreviewId(null);
    if (closeOpenPanel === closeRef.current) closeOpenPanel = null;
  }, [clearTimers, releasePin]);
  useEffect(() => {
    closeRef.current = close;
  }, [close]);

  const holdsPin = useCallback(() => {
    if (isPinned.current && !isOverlayOpen()) releasePin();
    return isPinned.current;
  }, [releasePin]);

  const scheduleHoverClose = useCallback(() => {
    clearTimeout(closeTimer.current);
    if (holdsPin()) return;
    closeTimer.current = setTimeout(close, CLOSE_DELAY_MS);
  }, [close, holdsPin]);

  const onRowHover = useCallback(
    (rowId: GridRowId | null) => {
      const id = rowId !== null && isPreviewable(String(rowId)) ? String(rowId) : null;
      hoveredId.current = id;
      if (!enabled || !peekEnabled || holdsPin()) return;
      clearTimeout(openTimer.current);
      if (id === null) {
        scheduleHoverClose();
        return;
      }
      clearTimeout(closeTimer.current);
      openTimer.current = setTimeout(
        () => show(id, 'peek_hover'),
        isOpen.current ? SWITCH_DELAY_MS : OPEN_DELAY_MS,
      );
    },
    [enabled, peekEnabled, holdsPin, isPreviewable, scheduleHoverClose, show],
  );

  const watchPinRelease = useCallback(
    (pointer: { x: number; y: number }) => {
      stopWatchingPin.current?.();
      let sawOverlay = false;
      const onPointerMove = (event: PointerEvent) => {
        pointer = { x: event.clientX, y: event.clientY };
      };
      const observer = new MutationObserver(() => {
        if (isOverlayOpen()) {
          sawOverlay = true;
          return;
        }
        if (!sawOverlay) return;
        releasePin();
        if (!peekEnabled) return;
        const under = document.elementFromPoint?.(pointer.x, pointer.y);
        if (under?.closest(`[${ROW_PREVIEW_PANEL_ATTRIBUTE}]`)) return;
        const row = under?.closest<HTMLElement>(ROW_SELECTOR);
        onRowHover(row && (scope === 'page' || ownsElement(row)) ? (row.dataset.id ?? null) : null);
      });
      observer.observe(document.body, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class'],
      });
      window.addEventListener('pointermove', onPointerMove, true);
      stopWatchingPin.current = () => {
        observer.disconnect();
        window.removeEventListener('pointermove', onPointerMove, true);
      };
    },
    [onRowHover, ownsElement, peekEnabled, releasePin, scope],
  );

  const pin = useCallback(
    (event?: { clientX: number; clientY: number }) => {
      if (!isOpen.current) return;
      clearTimers();
      isPinned.current = true;
      if (event) watchPinRelease({ x: event.clientX, y: event.clientY });
    },
    [clearTimers, watchPinRelease],
  );

  const panelHoverHandlers = {
    onMouseEnter: () => clearTimeout(closeTimer.current),
    // Opened with Peek mode off, the panel stays until it's closed.
    onMouseLeave: () => {
      if (peekEnabled) scheduleHoverClose();
    },
  };

  const getNeighbor = useCallback(
    (id: string | null, delta: 1 | -1): string | null => {
      if (!id) return null;
      let ids: GridRowId[];
      if (apiRef?.current?.state) {
        const api = apiRef.current;
        ids = gridExpandedSortedRowIdsSelector(apiRef).filter(
          (rowId) => api.getRowNode(rowId)?.type === 'leaf' && isPreviewable(String(rowId)),
        );
      } else if (rootRef) {
        ids = markedRows()
          .map((row) => row.dataset.id ?? '')
          .filter((rowId) => rowId !== '' && isPreviewable(rowId));
      } else {
        return null;
      }
      const index = ids.findIndex((rowId) => String(rowId) === id);
      const next = index === -1 ? undefined : ids[index + delta];
      return next === undefined ? null : String(next);
    },
    [apiRef, rootRef, isPreviewable, markedRows],
  );

  const step = useCallback(
    (delta: 1 | -1) => {
      const next = getNeighbor(currentId.current, delta);
      if (!next) return;
      clearTimers();
      show(next, 'keyboard');
      const api = apiRef?.current;
      const rowIndex = api?.getRowIndexRelativeToVisibleRows(next);
      if (api && rowIndex !== undefined && rowIndex >= 0) api.scrollToIndexes({ rowIndex });
      markedRows()
        .find((row) => row.dataset.id === next)
        ?.scrollIntoView?.({ block: 'nearest' });
    },
    [apiRef, clearTimers, getNeighbor, markedRows, show],
  );

  const onCellKeyDown = useCallback<GridEventListener<'cellKeyDown'>>(
    (params, event) => {
      if (!enabled || !keyboard || ownsKeys(event.target)) return;
      const id = String(params.id);
      if (event.key === ' ') {
        if (!isGridSpace(params.field, event.shiftKey)) event.defaultMuiPrevented = true;
        return;
      }
      if (!isOpen.current) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        const next = getNeighbor(id, event.key === 'ArrowDown' ? 1 : -1);
        if (next) {
          clearTimers();
          show(next, 'keyboard');
        }
      }
    },
    [enabled, keyboard, clearTimers, getNeighbor, show],
  );

  const openDocument = useCallback(
    (id: string) => {
      close();
      track('list_preview_opened', { list, trigger: 'document_link', record_id: id });
      setDocumentId(id);
    },
    [close, list, track],
  );

  const closeDocument = useCallback(() => setDocumentId(null), []);

  const setPeekEnabled = useCallback(
    (value: boolean) => {
      setPeekEnabledState(value);
      try {
        window.localStorage.setItem(storageKey, String(value));
      } catch {
        // Storage can be blocked; the switch still works for this visit.
      }
      window.dispatchEvent(
        new CustomEvent(PEEK_CHANGE_EVENT, { detail: { storageKey, enabled: value } }),
      );
      track('list_peek_mode_toggled', { list, enabled: value });
      if (!value) close();
    },
    [close, list, storageKey, track],
  );

  // Turning Peek mode on opens the hovered, or else focused, row at once.
  const togglePeekFromKeyboard = useCallback(
    (target: EventTarget | null) => {
      if (peekEnabled) {
        setPeekEnabled(false);
        return;
      }
      setPeekEnabled(true);
      const focusedRow =
        target instanceof Element
          ? (target.closest<HTMLElement>(ROW_SELECTOR)?.dataset.id ?? null)
          : null;
      const id = hoveredId.current ?? (focusedRow && isPreviewable(focusedRow) ? focusedRow : null);
      if (!id) return;
      clearTimers();
      show(id, 'keyboard');
    },
    [peekEnabled, setPeekEnabled, clearTimers, isPreviewable, show],
  );

  useEffect(() => {
    const onPeekChange = (event: Event) => {
      const { detail } = event as CustomEvent<{ storageKey: string; enabled: boolean }>;
      if (detail.storageKey !== storageKey) return;
      setPeekEnabledState(detail.enabled);
      if (!detail.enabled) close();
    };
    window.addEventListener(PEEK_CHANGE_EVENT, onPeekChange);
    return () => window.removeEventListener(PEEK_CHANGE_EVENT, onPeekChange);
  }, [storageKey, close]);

  useEffect(() => {
    if (!enabled || !keyboard) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (ownsKeys(event.target)) return;
      if (event.key === ' ') {
        if (event.repeat || isTabbedToControl(event.target) || ownsSpace(event.target)) return;
        if (isGridSpace(cellField(event.target), event.shiftKey)) return;
        const inUse = isOpen.current || hoveredId.current !== null || ownsElement(event.target);
        if ((scope === 'table' && !inUse) || handledSpace.has(event)) return;
        handledSpace.add(event);
        event.preventDefault();
        togglePeekFromKeyboard(event.target);
        return;
      }
      if (event.defaultPrevented) return;
      if (!isOpen.current) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.target instanceof Element && event.target.closest('.MuiDataGrid-root')) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        step(event.key === 'ArrowDown' ? 1 : -1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [enabled, keyboard, scope, ownsElement, close, step, togglePeekFromKeyboard]);

  return {
    previewId: enabled ? previewId : null,
    documentId: enabled ? documentId : null,
    closeDocument,
    peekEnabled: enabled && peekEnabled,
    setPeekEnabled,
    onRowHover: enabled ? onRowHover : undefined,
    onCellKeyDown: enabled ? onCellKeyDown : undefined,
    getNeighbor,
    step,
    openDocument,
    close,
    pin,
    panelHoverHandlers,
  };
}

export type RowPreview = ReturnType<typeof useRowPreview>;
